import styles from './search-help-modal.module.css'

import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import mixpanelClient from '@disco/api/mixpanel'
import React from 'react'

import {
  AccessibleIcon,
  Button,
  Dialog,
  DiscoCallout,
  Flex,
  IconButton,
  Table,
  Text,
} from 'components/ds'

const operators = [
  {
    label: 'Exact Match',
    description:
      'Enclose terms in " " to find an exact string, e.g. "a cappella"',
  },
  {
    label: 'Negative Search',
    description:
      'Use a minus in front of a search term to exclude keywords from results, e.g. -instrumental',
  },
  {
    label: 'Similarity Search',
    description:
      'Supports reference tracks from YouTube, Spotify, Apple Music & DISCO.',
  },
]

const goodToKnow = [
  {
    label: 'Equivalencies',
    description:
      'Our search engine treats short-form and long-form words as identical matches, e.g. "don\'t" and "do not", "one stop" and "one-stop".',
  },
  {
    label: 'Christmas already?',
    description:
      'We also treat "xmas" and "christmas" the same and you can search for either.',
  },
]

const handleOpenChange = (open: boolean) => {
  if (open) {
    mixpanelClient.sendPureEvent('Open modal', {
      Location: 'Explorer',
      Context: 'Search Page',
      Modal: 'Search Tips',
    })
  }
}

const SearchHelpModal = () => {
  return (
    // Flex positions the trigger at the far right of the search bar row
    <Flex ml="auto" align="center" pr="2">
      <Dialog.Root onOpenChange={handleOpenChange}>
        <Dialog.Trigger>
          <IconButton variant="ghost" color="gray" highContrast size="2">
            <AccessibleIcon label="Search help">
              <RxInfoCircled size={18} />
            </AccessibleIcon>
          </IconButton>
        </Dialog.Trigger>
        {/*
         * aria-describedby is set to undefined to suppress the Radix warning
         * about a missing Dialog.Description. This modal contains structured
         * content (table + callout) rather than a single description string,
         * so Dialog.Description is intentionally omitted.
         */}
        <Dialog.Content maxWidth="680px" aria-describedby={undefined}>
          <Dialog.Title>Search tips</Dialog.Title>

          <Flex direction="column" gap="4">
            <Text as="p" size="2" color="gray">
              There&apos;s more to search than meets the eye — here&apos;s how
              to get the most out of it:
            </Text>

            <Table.Root variant="surface">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Function</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {operators.map(op => (
                  <Table.Row key={op.label}>
                    <Table.Cell className={styles.typeColumn}>
                      <Text
                        size="2"
                        color="indigo"
                        weight="medium"
                        className={styles.typeLabel}
                      >
                        {op.label}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2">{op.description}</Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>

            <Flex direction="column" gap="2">
              <Text size="2" weight="bold">
                Good to know!
              </Text>
              <DiscoCallout
                type="warning"
                variant="soft"
                className={styles.callout}
                icon={<RxInfoCircled />}
              >
                <Flex direction="column" gap="3">
                  {goodToKnow.map(tip => (
                    <Flex key={tip.label} direction="column" gap="1">
                      <Text size="2" weight="bold">
                        {tip.label}
                      </Text>
                      <Text as="p" size="2">
                        {tip.description}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              </DiscoCallout>
            </Flex>
          </Flex>

          <Flex mt="5" justify="end">
            <Dialog.Close>
              <Button variant="solid" size="2">
                Close
              </Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Flex>
  )
}

export default SearchHelpModal
